import { useEffect, useState } from "react";
import type { TaxResults, TaxScenario, TaxScenarioInput } from "@/tax/types";
import { needsWithoutAltRun } from "@/lib/comparisonStory";

// ── Without-alt runs (comparison page v3) ────────────────────────────────────
// A strategy that carries an alt AND another lever needs the same strategy scored
// with the alt at zero, so the conversion's tax and the deduction's saving each get
// their own line. An alt-only strategy needs no call: its without-alt run is the
// baseline. Nothing here fires unless the page is on.

export type WithoutAltResults = Record<string, TaxResults>;

export function useWithoutAltResults(
  scenarios: TaxScenario[],
  baseScenario: TaxScenario | undefined,
  compute: (input: TaxScenarioInput) => Promise<TaxResults>,
  enabled: boolean,
): WithoutAltResults {
  const [results, setResults] = useState<WithoutAltResults>({});
  const key = enabled && baseScenario
    ? scenarios.filter((s) => needsWithoutAltRun(s, baseScenario)).map((s) => `${s.id}:${s.results.totalEconomicCost}`).join("|")
    : "";
  useEffect(() => {
    if (!enabled || !baseScenario) return;
    let cancelled = false;
    const next: WithoutAltResults = {};
    const pending = scenarios
      .filter((s) => needsWithoutAltRun(s, baseScenario))
      .map((s) =>
        compute({ ...s.input, alternativeInvestmentAmount: 0 })
          .then((without) => { if (typeof without?.totalTaxLiability === "number") next[s.id] = without; })
          .catch(() => {}),
      );
    if (pending.length === 0) return;
    Promise.all(pending).then(() => { if (!cancelled) setResults(next); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return results;
}

